import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Compass, HandHeart, Route, Sparkles, GraduationCap, Briefcase,
  HeartPulse, Cpu, TrendingUp, Users, Wrench, Utensils, Palette, BookOpen,
  Clock, Monitor, Sunrise, Sun, Zap, CalendarDays, CheckCircle2, Search,
  ClipboardList, DollarSign, FileCheck, Rocket, ShieldCheck, ChevronDown,
} from 'lucide-react';
import PersonaContactModal from '../components/PersonaContactModal';

const IMG_HERO = 'https://images.unsplash.com/photo-1760351561007-526f5353cc76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxtYXR1cmUlMjBzdHVkZW50JTIwbW9kZXJuJTIwY2FtcHVzfGVufDB8fHx8MTc4NDIzODMwOHww&ixlib=rb-4.1.0&q=85';
const IMG_TESTI = 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsJTIwc21pbGluZ3xlbnwwfHx8fDE3ODQyMzgzMDl8MA&ixlib=rb-4.1.0&q=85';
const IMG_RESOURCE = 'https://images.unsplash.com/photo-1538688423619-a81d3f23454b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFscyUyMGNvbGxhYm9yYXRpbmd8ZW58MHx8fHwxNzg0MjM4MzA5fDA&ixlib=rb-4.1.0&q=85';

const CONFIDENCE = [
  'Personalized Education Planning',
  'Canadian Colleges & Universities',
  'Flexible Learning Options',
  'Support from Start to Enrolment',
];

const JOURNEYS = [
  { icon: GraduationCap, title: "I'm Starting My Career", desc: 'Explore programs designed for new graduates and first-time students.' },
  { icon: Route, title: "I'm Ready for a Career Change", desc: 'Find education pathways that fit your experience and future goals.' },
  { icon: Sparkles, title: 'I Want to Upgrade My Skills', desc: 'Short-term certificates, diplomas, and professional development options.' },
  { icon: Compass, title: 'I Need Help Choosing', desc: "Not sure where to begin? We'll help you compare programs and build a plan." },
];

const MODALITIES = [
  { icon: Sun, label: 'Full-Time Study' },
  { icon: Sunrise, label: 'Evening Programs' },
  { icon: CalendarDays, label: 'Weekend Classes' },
  { icon: Monitor, label: 'Online Learning' },
  { icon: Zap, label: 'Hybrid Programs' },
  { icon: Clock, label: 'Accelerated Programs' },
];

const PATHWAYS = [
  { icon: HeartPulse, name: 'Healthcare' },
  { icon: Cpu, name: 'Technology' },
  { icon: TrendingUp, name: 'Business' },
  { icon: BookOpen, name: 'Education' },
  { icon: Users, name: 'Community Services' },
  { icon: Wrench, name: 'Trades' },
  { icon: Utensils, name: 'Hospitality' },
  { icon: Palette, name: 'Creative Industries' },
];

const HOW_STEPS = [
  { n: '01', icon: Search, title: 'Learn about your goals', body: 'A conversation, not a form. We start where you are — no assumptions.' },
  { n: '02', icon: Compass, title: 'Explore suitable education options', body: 'Pathways that fit your life, your responsibilities, your budget.' },
  { n: '03', icon: ClipboardList, title: 'Compare programs and institutions', body: 'Real cost. Real outcomes. Side-by-side, no marketing fluff.' },
  { n: '04', icon: DollarSign, title: 'Understand tuition and funding', body: 'Loans, grants, sponsorships — we break it down in plain English.' },
  { n: '05', icon: FileCheck, title: 'Complete your application', body: "We handle the paperwork so you can focus on what's next." },
  { n: '06', icon: Rocket, title: 'Prepare for your first day of class', body: "You're not on your own after admission. We stay in your corner." },
];

const TRUST = [
  { icon: HandHeart, title: 'Personal Guidance', body: 'You get an advisor, not a call-centre.' },
  { icon: ShieldCheck, title: 'Clear Information', body: 'Transparent costs. Honest outcomes. No pressure.' },
  { icon: Compass, title: 'Flexible Options', body: 'Programs shaped around your work and family.' },
  { icon: Users, title: 'Ongoing Support', body: 'From first call to your first day of class.' },
  { icon: Briefcase, title: 'Local Knowledge', body: 'Deep understanding of Canadian institutions.' },
  { icon: CheckCircle2, title: 'Trusted Partnerships', body: 'Direct relationships with recognised colleges.' },
];

const FAQ_THEMES = [
  { key: 'program', label: 'Choosing a Program' },
  { key: 'funding', label: 'Funding' },
  { key: 'admissions', label: 'Admissions' },
  { key: 'online', label: 'Online Learning' },
  { key: 'career', label: 'Career Outcomes' },
  { key: 'life', label: 'Student Life' },
];
const FAQS = {
  program: [
    ['I don\u2019t know what to study. Can you help?', 'Yes — that\u2019s literally what we do. An advisor sits with you, understands where you are and where you want to go, then maps 2\u20133 program options to compare.'],
    ['Am I too old to go back to school?', 'No. Most of the students we help are 25\u201350. Many programs are designed specifically for working adults.'],
    ['How long do most programs take?', 'From 6-month certificates to 4-year degrees. We\u2019ll show you options at every length.'],
  ],
  funding: [
    ['Can I afford it?', 'Almost everyone can, once they see the funding options. We walk you through loans, grants, and employer sponsorships upfront.'],
    ['Do you help with student aid applications?', 'Yes. We help you understand eligibility and guide the paperwork.'],
  ],
  admissions: [
    ['What documents do I need?', 'Usually transcripts, ID, and (sometimes) English proficiency. We\u2019ll give you a checklist personalised to your program.'],
    ['How long does admission take?', 'Two to eight weeks depending on the college.'],
  ],
  online: [
    ['Are online programs recognised?', 'Yes, when they\u2019re run by accredited Canadian institutions. We only work with recognised colleges.'],
  ],
  career: [
    ['Will this program actually get me a job?', 'We show you employment outcomes for every program we recommend, not just brochure numbers.'],
  ],
  life: [
    ['Can I work while I study?', 'Yes. Evening, weekend, hybrid, and part-time options exist for exactly this reason.'],
  ],
};

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 0.9, 0.32, 1] } }) };

export default function HomePage() {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState('program');
  const [openIdx, setOpenIdx] = useState(0);
  const [personaModal, setPersonaModal] = useState(null); // { title, desc } | null

  return (
    <div className="bg-brand-alabaster font-sans-clean text-brand-obsidian" data-testid="home-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-8">
              <span className="w-8 h-px bg-brand-coral" /> Your Future. Your Career. Your Education.
            </div>
            <h1 className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-brand-obsidian">
              Build the career you want. <span className="text-brand-pine italic">It starts with the right education.</span>
            </h1>
            <p className="mt-8 text-lg text-brand-slate leading-relaxed max-w-xl">
              Whether you&apos;re starting fresh, changing careers, or upgrading your skills, our advisors help you choose the right program, understand your options, and take the next step with confidence.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={() => navigate('/contact')}
                data-testid="hero-find-program"
                className="inline-flex items-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm font-semibold px-7 py-4 rounded-full transition-colors"
              >
                Find My Best Program <ArrowRight size={16} />
              </button>
              <button
                onClick={() => navigate('/contact')}
                data-testid="hero-talk-advisor"
                className="inline-flex items-center gap-2 border border-brand-obsidian/15 hover:border-brand-pine text-brand-obsidian text-sm font-semibold px-7 py-4 rounded-full transition-colors"
              >
                Talk to an Advisor
              </button>
            </div>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/5]">
              <img src={IMG_HERO} alt="A student planning their next step" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-obsidian/40 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white shadow-xl rounded-2xl px-5 py-4 max-w-[220px] hidden md:block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-pine font-bold">Advisor session</div>
              <div className="font-serif-editorial text-lg mt-1 leading-tight">Free 30-min planning call</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CONFIDENCE BAR */}
      <div className="border-y border-brand-border/50 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-y-3 divide-x divide-brand-border/50">
          {CONFIDENCE.map((c) => (
            <div key={c} className="px-4 flex items-center gap-2 text-xs md:text-sm text-brand-obsidian/80">
              <CheckCircle2 size={14} className="text-brand-coral flex-shrink-0" /> {c}
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 2 — EVERY JOURNEY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">Where you are</div>
            <h2 className="font-serif-editorial text-3xl md:text-5xl tracking-tight max-w-xl leading-tight">Every journey starts somewhere.</h2>
          </div>
          <p className="text-brand-slate max-w-md">Pick the one that sounds like you. We&apos;ll take it from there.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {JOURNEYS.map((j, i) => (
            <motion.button
              type="button"
              key={j.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-80px' }}
              custom={i}
              onClick={() => setPersonaModal({ title: j.title, desc: j.desc })}
              className="group text-left bg-white border border-brand-border/50 rounded-2xl p-7 hover:-translate-y-1 hover:border-brand-pine/40 hover:shadow-lg transition-[transform,box-shadow,border-color] cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-coral/40 focus:ring-offset-2"
              data-testid={`journey-card-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-brand-pine/5 group-hover:bg-brand-coral/10 flex items-center justify-center transition-colors">
                <j.icon size={20} className="text-brand-pine group-hover:text-brand-coral transition-colors" />
              </div>
              <h3 className="font-serif-editorial text-xl mt-6 leading-snug">{j.title}</h3>
              <p className="text-sm text-brand-slate mt-3 leading-relaxed">{j.desc}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-pine group-hover:text-brand-coral-deep transition-colors">
                Talk to an advisor <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* SECTION 3 — MODALITIES */}
      <section className="bg-white border-y border-brand-border/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">Fits your life</div>
            <h2 className="font-serif-editorial text-3xl md:text-5xl tracking-tight leading-tight">Education that works around you.</h2>
            <p className="text-brand-slate mt-5">Everyone&apos;s situation is different. Options that work with your schedule, goals, and responsibilities.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-brand-border/40 border border-brand-border/40 rounded-2xl overflow-hidden">
            {MODALITIES.map((m) => (
              <div
                key={m.label}
                className="bg-white p-8 group hover:bg-brand-mist transition-colors"
                data-testid={`modality-${m.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <m.icon size={26} className="text-brand-coral" />
                <div className="font-serif-editorial text-lg mt-5 leading-snug">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — PATHWAYS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">Outcomes, not brochures</div>
            <h2 className="font-serif-editorial text-3xl md:text-5xl tracking-tight leading-tight max-w-2xl">Explore career pathways.</h2>
          </div>
          <button onClick={() => navigate('/pathways')} className="text-sm text-brand-pine font-semibold inline-flex items-center gap-2 hover:text-brand-coral transition-colors">
            View all pathways <ArrowRight size={14} />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-border/40 border border-brand-border/40 rounded-2xl overflow-hidden">
          {PATHWAYS.map((p) => (
            <button
              key={p.name}
              onClick={() => navigate('/pathways')}
              className="bg-white p-8 text-left group hover:bg-brand-pine hover:text-white transition-colors"
              data-testid={`pathway-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <p.icon size={22} className="text-brand-coral group-hover:text-brand-coral" />
              <div className="font-serif-editorial text-lg mt-5 leading-snug">{p.name}</div>
              <div className="mt-4 inline-flex items-center gap-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                Explore <ArrowRight size={12} />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* SECTION 5 — HOW WE HELP TIMELINE */}
      <section className="bg-brand-obsidian text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-coral font-bold mb-4">How we help</div>
              <h2 className="font-serif-editorial text-3xl md:text-5xl tracking-tight leading-tight">Guidance from goal to first day of class.</h2>
              <p className="text-white/60 mt-6 leading-relaxed">Six steps, one advisor. No hand-offs, no queues, no pressure.</p>
            </div>
            <div className="lg:col-span-8 relative">
              <div className="absolute left-6 top-2 bottom-2 w-px bg-white/10 hidden md:block" />
              <div className="space-y-14">
                {HOW_STEPS.map((s, i) => (
                  <motion.div
                    key={s.n}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    custom={i}
                    className="flex gap-6 md:gap-10 relative"
                    data-testid={`how-step-${s.n}`}
                  >
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-brand-coral flex items-center justify-center relative z-10">
                        <s.icon size={18} className="text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-editorial text-5xl text-white/10 leading-none">{s.n}</div>
                      <h3 className="font-serif-editorial text-2xl md:text-3xl mt-4 leading-tight">{s.title}</h3>
                      <p className="text-white/60 mt-3 max-w-md leading-relaxed">{s.body}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — TRUST PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="max-w-2xl mb-14">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">Why students trust True Global</div>
          <h2 className="font-serif-editorial text-3xl md:text-5xl tracking-tight leading-tight">Not because we process applications.</h2>
          <p className="text-brand-slate mt-5 text-lg">Because we take time to understand your goals.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
          {TRUST.map((t, i) => (
            <motion.div
              key={t.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-60px' }}
              custom={i}
            >
              <t.icon size={22} className="text-brand-coral" />
              <h3 className="font-serif-editorial text-xl mt-5 leading-snug">{t.title}</h3>
              <p className="text-sm text-brand-slate mt-3 leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SECTION 7 — TESTIMONIAL */}
      <section className="bg-brand-pine text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 relative">
            <div className="rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
              <img src={IMG_TESTI} alt="A healthcare professional" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-coral font-bold">Real stories. Real progress.</div>
            <blockquote className="font-serif-editorial text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight mt-6">
              <span className="text-brand-coral text-6xl leading-none font-bold align-top mr-1">“</span>
              After years in retail, I wanted a career with more stability. My advisor helped me compare healthcare programs and choose one that fit my schedule. Today I&apos;m working in a role I genuinely enjoy.
            </blockquote>
            <div className="mt-8 flex items-center gap-4">
              <div>
                <div className="font-serif-editorial text-lg">Priya, 34</div>
                <div className="text-white/60 text-sm">Retail → Personal Support Worker · Ontario</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — RESOURCES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="max-w-2xl mb-14">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">Before you commit</div>
          <h2 className="font-serif-editorial text-3xl md:text-5xl tracking-tight leading-tight">Resources that help you decide.</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div
            className="lg:col-span-2 lg:row-span-2 relative rounded-3xl overflow-hidden min-h-[420px] group cursor-pointer"
            onClick={() => navigate('/resources')}
            data-testid="resource-featured"
          >
            <img src={IMG_RESOURCE} alt="Career planning guide" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-obsidian to-transparent" />
            <div className="absolute inset-0 p-10 flex flex-col justify-end">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-coral font-bold mb-3">Featured Guide</div>
              <h3 className="font-serif-editorial text-3xl md:text-4xl text-white leading-tight max-w-md">Career Planning Guide</h3>
              <p className="text-white/70 mt-3 max-w-sm text-sm">The 20-minute read that has helped 500+ Canadians pick a career direction they don&apos;t regret.</p>
              <button className="mt-6 self-start inline-flex items-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors">
                Read guide <ArrowRight size={14} />
              </button>
            </div>
          </div>
          {[
            ['Funding Basics', 'What loans, grants and employer sponsorships actually cost you.'],
            ['Program Comparison Tool', 'See two programs side-by-side, outcomes included.'],
            ['Questions to Ask a College', "A checklist so you're not caught off-guard on your info call."],
            ['Education Planning Checklist', 'From transcripts to first-day prep — everything in order.'],
          ].map(([t, d], i) => (
            <div key={t} onClick={() => navigate('/resources')} className="bg-white border border-brand-border/50 rounded-2xl p-6 cursor-pointer hover:-translate-y-1 hover:border-brand-pine/40 transition-[transform,border-color]" data-testid={`resource-${i}`}>
              <div className="w-10 h-10 bg-brand-pine/5 rounded-lg flex items-center justify-center">
                <BookOpen size={16} className="text-brand-pine" />
              </div>
              <h3 className="font-serif-editorial text-lg mt-5 leading-snug">{t}</h3>
              <p className="text-xs text-brand-slate mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 9 — FAQ */}
      <section id="faq" className="bg-white border-y border-brand-border/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">FAQs</div>
              <h2 className="font-serif-editorial text-3xl md:text-5xl tracking-tight leading-tight">Straight answers. No small print.</h2>
              <div className="mt-10 flex flex-col gap-1">
                {FAQ_THEMES.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => { setActiveFaq(t.key); setOpenIdx(0); }}
                    data-testid={`faq-tab-${t.key}`}
                    className={`text-left py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                      activeFaq === t.key
                        ? 'bg-brand-pine text-white'
                        : 'text-brand-obsidian/70 hover:bg-brand-mist'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="divide-y divide-brand-border/50">
                {(FAQS[activeFaq] || []).map(([q, a], i) => {
                  const open = openIdx === i;
                  return (
                    <div key={q} className="py-5">
                      <button
                        onClick={() => setOpenIdx(open ? -1 : i)}
                        className="w-full flex items-start justify-between gap-6 text-left"
                        data-testid={`faq-q-${i}`}
                      >
                        <span className="font-serif-editorial text-lg md:text-xl leading-snug">{q}</span>
                        <ChevronDown size={18} className={`text-brand-pine flex-shrink-0 mt-1 transition-transform ${open ? 'rotate-180' : ''}`} />
                      </button>
                      {open && (
                        <p className="text-brand-slate mt-4 leading-relaxed max-w-2xl">{a}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-pine">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-coral font-bold mb-6">Next step</div>
          <h2 className="font-serif-editorial text-4xl md:text-6xl text-white leading-[1.05] tracking-tight max-w-3xl mx-auto">
            Your next step doesn&apos;t have to be complicated.
          </h2>
          <p className="text-white/70 mt-6 max-w-xl mx-auto text-lg leading-relaxed">
            Choosing your education is one of the biggest decisions you&apos;ll make. Our advisors are here to help you understand your options and move forward with confidence.
          </p>
          <button
            onClick={() => navigate('/contact')}
            data-testid="final-cta-book"
            className="mt-10 inline-flex items-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm md:text-base font-semibold px-8 py-4 rounded-full transition-colors"
          >
            Book a Free Consultation <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <PersonaContactModal
        open={!!personaModal}
        persona={personaModal?.title}
        personaDesc={personaModal?.desc}
        onClose={() => setPersonaModal(null)}
      />
    </div>
  );
}
